import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, CalendarClock, Truck, History, ChevronRight,
  UserPlus, ListPlus, Boxes, Bot, MessagesSquare, Coins, Gauge, PackagePlus
} from 'lucide-react';

import { operationsApi } from '../../api/operations.js';
import { analyticsApi } from '../../api/analytics.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { fmt } from '../../lib/formatters.js';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Buongiorno';
  if (h < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

function relTime(at) {
  if (!at) return '';
  const diff = Date.now() - new Date(at).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'ora';
  if (m < 60) return `${m} min fa`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h fa`;
  return new Date(at).toLocaleDateString('it-IT');
}

const TONES = {
  sage: { text: 'text-brand', soft: 'bg-brand-soft', dot: 'bg-brand' },
  amber: { text: 'text-warning', soft: 'bg-warning/10', dot: 'bg-warning' },
  slate: { text: 'text-info', soft: 'bg-info/10', dot: 'bg-info' },
  rose: { text: 'text-danger', soft: 'bg-danger/10', dot: 'bg-danger' }
};

/** Compact, number-forward operational tile. The whole tile is a click target. */
function StatTile({ icon: Icon, label, count = 0, tone = 'sage', to, onNavigate }) {
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={() => to && onNavigate(to)}
      className="group flex items-center gap-3 rounded-xl border border-border bg-surface-elevated p-3.5 text-left transition-all hover:border-brand/40 hover:shadow-elevated"
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${t.soft} ${t.text}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <span className="text-xl font-semibold tabular-nums leading-none text-ink">{count}</span>
        <p className="mt-0.5 truncate text-xs text-ink-muted">{label}</p>
      </div>
    </button>
  );
}

function Block({ icon: Icon, title, count, tone = 'sage', to, children, onNavigate, empty }) {
  const t = TONES[tone];
  return (
    <Card padded={false} className="flex flex-col">
      <button
        type="button"
        onClick={() => to && onNavigate(to)}
        className="group flex items-center justify-between gap-2 border-b border-border px-4 py-3 text-left transition-colors hover:bg-surface-subtle/60"
      >
        <div className="flex items-center gap-2.5">
          <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${t.soft} ${t.text}`}>
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {typeof count === 'number' && count > 0 && (
            <span className={`rounded-full ${t.soft} px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${t.text}`}>{count}</span>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-0.5" />
      </button>
      <div className="flex-1 px-4 py-3">
        {children || <p className="text-sm text-ink-faint">{empty || 'Nulla da segnalare.'}</p>}
      </div>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value, sub, loading }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-elevated p-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        {loading ? <Skeleton className="h-5 w-16" /> : <span className="text-lg font-semibold tabular-nums text-ink">{value}</span>}
        <p className="truncate text-xs text-ink-muted">{label}{sub ? ` · ${sub}` : ''}</p>
      </div>
    </div>
  );
}

export function EmployeeHomePage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const go = (to) => navigate(to);
  const name = (user && (user.firstName || (user.email && user.email.split('@')[0]))) || '';

  const ops = useQuery({ queryKey: ['employee', 'operations'], queryFn: operationsApi.summary });
  const s = ops.data || {};

  const canStats = hasPermission('analytics:read');
  const stats = useQuery({
    queryKey: ['employee', 'self-stats'],
    enabled: canStats && Boolean(user && user._id),
    queryFn: () => analyticsApi.employeeReport(user._id, { period: '30d' })
  });
  const sd = stats.data;

  const QUICK = [
    hasPermission('customers:create') && { label: 'Nuovo cliente', icon: UserPlus, to: '/employee/customers' },
    hasPermission('tasks:create') && { label: 'Nuova attività', icon: ListPlus, to: '/employee/tasks' },
    hasPermission('products:read') && { label: 'Inventario', icon: Boxes, to: '/employee/inventory' },
    hasPermission('ai:chat') && { label: 'Assistente AI', icon: Bot, to: '/employee/chat' }
  ].filter(Boolean);

  const tiles = [
    { key: 'attention', icon: AlertTriangle, label: 'Da gestire', count: s.attention?.count, tone: 'rose', to: '/employee/tasks' },
    { key: 'followUps', icon: CalendarClock, label: 'Follow-up', count: s.followUps?.count, tone: 'slate', to: '/employee/customers' },
    { key: 'logistics', icon: Truck, label: 'Logistica', count: s.logistics?.count, tone: 'amber', to: '/employee/logistics' },
    { key: 'recent', icon: History, label: 'Attività recenti', count: (s.recent || []).length, tone: 'sage', to: '/employee/tasks' }
  ];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{greeting()} {name}</h1>
        <p className="mt-1 text-sm text-ink-muted">Ecco cosa richiede attenzione nel tuo negozio oggi.</p>
      </div>

      {QUICK.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {QUICK.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.label} to={a.to} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-elevated px-3 py-1.5 text-sm text-ink transition-colors hover:border-brand/40 hover:bg-brand-soft">
                <Icon className="h-4 w-4 text-brand" strokeWidth={1.75} />{a.label}
              </Link>
            );
          })}
        </div>
      )}

      {ops.isLoading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[68px] w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tiles.map((t) => <StatTile key={t.key} {...t} onNavigate={go} />)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Block icon={AlertTriangle} title="Da gestire ora" tone="rose" count={s.attention?.count} to="/employee/tasks" onNavigate={go} empty="Niente di urgente. Tutto sotto controllo.">
            {s.attention?.items?.length > 0 && (
              <ul className="-my-1 flex flex-col divide-y divide-border/60">
                {s.attention.items.slice(0, 5).map((it, i) => {
                  const overdue = it.dueAt && new Date(it.dueAt) < new Date();
                  return (
                    <li key={it.id || i} onClick={() => go('/employee/tasks')} className="flex cursor-pointer items-center gap-2.5 py-2">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${it.priority === 'high' ? 'bg-danger' : 'bg-warning'}`} />
                      <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{it.title}</span>
                      {it.dueAt && <span className={`shrink-0 text-xs tabular-nums ${overdue ? 'font-medium text-danger' : 'text-ink-faint'}`}>{new Date(it.dueAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </Block>

          <Block icon={CalendarClock} title="Follow-up in scadenza" tone="slate" count={s.followUps?.count} to="/employee/customers" onNavigate={go} empty="Nessun richiamo in scadenza.">
            {s.followUps?.items?.length > 0 && (
              <ul className="-my-1 flex flex-col divide-y divide-border/60">
                {s.followUps.items.slice(0, 5).map((f, i) => (
                  <li key={f.id || i} onClick={() => go('/employee/customers')} className="flex cursor-pointer items-center gap-2.5 py-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-info" />
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{f.label}</span>
                    {f.date && <span className="shrink-0 text-xs tabular-nums text-ink-faint">{new Date(f.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </div>

        <div className="flex flex-col gap-4">
          <Block icon={Truck} title="Logistica" tone="amber" count={s.logistics?.count} to="/employee/logistics" onNavigate={go} empty="Nessun movimento in sospeso.">
            {(s.logistics?.transfers?.length > 0 || s.logistics?.loads?.length > 0) && (
              <ul className="-my-1 flex flex-col divide-y divide-border/60">
                {[...(s.logistics?.transfers || []), ...(s.logistics?.loads || [])].slice(0, 6).map((x, i) => (
                  <li key={x.id || i} onClick={() => go('/employee/logistics')} className="flex cursor-pointer items-center gap-2.5 py-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{x.from ? `${x.from} → ${x.to}` : `Carico ${x.store || ''}`}</span>
                    <span className="shrink-0 rounded-full bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-faint">{x.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </Block>

          <Block icon={History} title="Attività recenti" tone="slate" to="/employee/tasks" onNavigate={go} empty="Nessuna attività recente.">
            {s.recent?.length > 0 && (
              <ul className="flex flex-col gap-3">
                {s.recent.slice(0, 6).map((r, i) => (
                  <li key={r.id || i} className="flex items-start gap-2.5">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-border" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-ink-muted">{r.label}</p>
                      <p className="text-xs text-ink-faint">{relTime(r.at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </div>
      </div>

      {canStats && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">La tua attività · ultimi 30 giorni</p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MiniStat icon={MessagesSquare} label="Conversazioni AI" loading={stats.isLoading} value={sd ? fmt.int(sd.conversations) : '-'} sub={sd ? `${fmt.int(sd.aiCalls)} chiamate` : null} />
            <MiniStat icon={Coins} label="Token" loading={stats.isLoading} value={sd ? fmt.int(sd.totalTokens) : '-'} />
            <MiniStat icon={Gauge} label="Latenza media" loading={stats.isLoading} value={sd ? fmt.durationMs(sd.avgLatencyMs) : '-'} />
            <MiniStat icon={PackagePlus} label="Prodotti creati" loading={stats.isLoading} value={sd ? fmt.int(sd.productsCreatedInPeriod) : '-'} />
          </div>
        </div>
      )}
    </div>
  );
}
